import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Modal from "../Elements/Modal/Modal";
import MainButton from "../Elements/MainButton/MainButton";
import SecondButton from "../Elements/SecondButton/SecondButton";
import MainInput from "../Elements/MainInput/MainInput";
import AffiliationModalStatus from "./AffiliationModalStatus";
import AffiliationService from "../../services/AffiliationService";
import { getMinDateTimeLocal, isFutureDate } from "./promocodeDate";
import "../AdminPages/AdminModalForm.css";

const CURRENCIES = ["USD", "EUR", "GBP", "AED", "AUD"];
const PLN_AMOUNTS = [5, 10, 15, 20, 25];
const OTHER_AMOUNTS = [1, 2, 3, 4, 5];
const DISCOUNTS = [5, 10, 15];

const initialFormData = {
  code: "",
  type: "topup_amount",
  amount: "",
  currency: "EUR",
  date_end: "",
  activations_left: "",
};

const AffiliationCreatePromocodeModal = ({ isOpen, onClose, onCreated }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState(initialFormData);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const isDiscount = formData.type === "sell_discount";
  const amountOptions = isDiscount ? DISCOUNTS : formData.currency === "PLN" ? PLN_AMOUNTS : OTHER_AMOUNTS;

  useEffect(() => {
    if (isOpen) {
      setFormData(initialFormData);
      setError("");
      setSuccess("");
    }
  }, [isOpen]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((previous) => {
      const next = { ...previous, [name]: value };
      if (name === "type" || name === "currency") {
        next.amount = "";
      }
      return next;
    });
    setError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (saving) return;

    const amount = Number(formData.amount);
    const activationsLeft = Number(formData.activations_left);
    const allowedAmounts = isDiscount ? DISCOUNTS : formData.currency === "PLN" ? PLN_AMOUNTS : OTHER_AMOUNTS;
    const expirationDate = new Date(formData.date_end);

    if (
      !formData.code.trim() ||
      !allowedAmounts.includes(amount) ||
      !formData.date_end ||
      Number.isNaN(expirationDate.getTime())
    ) {
      setError(
        t(
          "affiliation.promocodeInvalidForm",
          "Complete all required fields and select a valid amount and expiration date.",
        ),
      );
      return;
    }

    if (!isFutureDate(expirationDate)) {
      setError(t("affiliation.promocodeDateMustBeFuture", "Expiration date must be in the future."));
      return;
    }

    if (!Number.isInteger(activationsLeft) || activationsLeft < 1) {
      setError(t("affiliation.promocodeInvalidActivations", "Activations left must be a positive whole number."));
      return;
    }

    try {
      setSaving(true);
      setError("");
      const payload = {
        code: formData.code.trim().toUpperCase(),
        type: formData.type,
        amount,
        currency: isDiscount ? null : formData.currency,
        date_end: expirationDate.toISOString(),
        activations_left: activationsLeft,
        user_activation_count: 1,
      };

      await AffiliationService.createAffiliatePromocode(payload);
      setSuccess(t("affiliation.promocodeCreatedSuccessfully", "Promocode created successfully."));
      onCreated?.();
    } catch (requestError) {
      const detail = requestError.response?.data?.detail;
      setError(
        typeof detail === "string" ? detail : requestError.message || t("errors.generic", "Something went wrong"),
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={saving ? () => {} : onClose}
      title={t("affiliation.createPromocode", "Create Promocode")}
      actions={
        success ? (
          <MainButton onClick={onClose}>{t("common.close", "Close")}</MainButton>
        ) : (
          <>
            <SecondButton onClick={onClose} disabled={saving}>
              {t("common.cancel", "Cancel")}
            </SecondButton>
            <MainButton onClick={handleSubmit} disabled={saving}>
              {saving ? t("common.creating", "Creating...") : t("affiliation.createPromocode", "Create Promocode")}
            </MainButton>
          </>
        )
      }
    >
      <form id="affiliate-promocode-form" onSubmit={handleSubmit} className="admin-modal-form">
        <fieldset className="admin-modal-fieldset" disabled={Boolean(success)}>
          <label>
            {t("affiliation.promocodeCode", "Code")} *
            <MainInput
              type="text"
              name="code"
              value={formData.code}
              onChange={handleChange}
              required
              disabled={saving}
              autoComplete="off"
            />
          </label>

          <label>
            {t("affiliation.promocodeType", "Type")} *
            <div className="admin-modal-select-wrapper">
              <select
                name="type"
                value={formData.type}
                onChange={handleChange}
                disabled={saving}
                className="admin-modal-select admin-modal-select-with-arrow"
              >
                <option value="topup_amount">{t("affiliation.promocodeTopupAmount", "Top-up amount")}</option>
                <option value="sell_discount">{t("affiliation.promocodeSellDiscount", "Sell discount")}</option>
              </select>
            </div>
          </label>

          {!isDiscount && (
            <label>
              {t("affiliation.currency", "Currency")} *
              <div className="admin-modal-select-wrapper">
                <select
                  name="currency"
                  value={formData.currency}
                  onChange={handleChange}
                  disabled={saving}
                  className="admin-modal-select admin-modal-select-with-arrow"
                >
                  {CURRENCIES.map((currency) => (
                    <option key={currency} value={currency}>
                      {currency}
                    </option>
                  ))}
                </select>
              </div>
            </label>
          )}

          <label>
            {isDiscount ? t("affiliation.discountPercent", "Discount (%)") : t("affiliation.promocodeAmount", "Amount")}{" "}
            *
            <div className="admin-modal-select-wrapper">
              <select
                name="amount"
                value={formData.amount}
                onChange={handleChange}
                required
                disabled={saving}
                className="admin-modal-select admin-modal-select-with-arrow"
              >
                <option value="">{t("common.selectOption", "Select an amount")}</option>
                {amountOptions.map((amount) => (
                  <option key={amount} value={amount}>
                    {amount}
                    {isDiscount ? "%" : ` ${formData.currency}`}
                  </option>
                ))}
              </select>
            </div>
          </label>

          <label>
            {t("affiliation.promocodeExpiresAt", "Expires at")} *
            <MainInput
              type="datetime-local"
              name="date_end"
              value={formData.date_end}
              onChange={handleChange}
              required
              disabled={saving}
              min={getMinDateTimeLocal()}
            />
          </label>

          <label>
            {t("affiliation.activationsLeft", "Activations left")} *
            <MainInput
              type="number"
              name="activations_left"
              value={formData.activations_left}
              onChange={handleChange}
              required
              disabled={saving}
              min="1"
              step="1"
            />
          </label>

          <label>
            {t("affiliation.usesPerUser", "Uses per user")}
            <MainInput type="number" value="1" disabled />
          </label>
        </fieldset>
        <AffiliationModalStatus
          error={error}
          success={success}
          onErrorClose={() => setError("")}
          onSuccessClose={onClose}
        />
      </form>
    </Modal>
  );
};

export default AffiliationCreatePromocodeModal;
